import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import LinkExt from '@tiptap/extension-link';
import TextAlign from '@tiptap/extension-text-align';
import Placeholder from '@tiptap/extension-placeholder';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Bold,
  Italic,
  Underline as UnderlineIcon,
  Strikethrough,
  List,
  ListOrdered,
  Link as LinkIcon,
  AlignLeft,
  AlignCenter,
  AlignRight,
  Heading2,
  Heading3,
  Undo,
  Redo,
} from 'lucide-react';

interface RichTextEditorProps {
  value?: string;
  onChange?: (html: string) => void;
  placeholder?: string;
  label?: string;
  error?: string;
}

export function RichTextEditor({ value, onChange, placeholder, label, error }: RichTextEditorProps) {
  const { t } = useTranslation();
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [2, 3] },
      }),
      Underline,
      LinkExt.configure({
        openOnClick: false,
        HTMLAttributes: { target: '_blank', rel: 'noopener noreferrer' },
      }),
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      Placeholder.configure({ placeholder: placeholder || '' }),
    ],
    content: value || '',
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      onChange?.(html === '<p></p>' ? '' : html);
    },
    editorProps: {
      attributes: {
        class: 'prose prose-sm max-w-none min-h-[120px] px-3 py-2.5 text-sm outline-none text-text-primary [&_p]:my-1 [&_h2]:text-lg [&_h2]:font-bold [&_h2]:mt-3 [&_h2]:mb-1 [&_h3]:text-base [&_h3]:font-semibold [&_h3]:mt-2 [&_h3]:mb-1 [&_ul]:list-disc [&_ul]:ps-5 [&_ol]:list-decimal [&_ol]:ps-5 [&_a]:text-accent [&_a]:underline',
      },
    },
  });

  // Sync external value changes (e.g., form reset / product load)
  useEffect(() => {
    if (editor && value !== undefined) {
      const current = editor.getHTML();
      if (current !== value && !(current === '<p></p>' && !value)) {
        editor.commands.setContent(value || '', { emitUpdate: false });
      }
    }
  }, [value, editor]);

  if (!editor) return null;

  const btnClass = (active: boolean) =>
    `rounded p-1.5 transition-colors ${
      active
        ? 'bg-accent/15 text-accent'
        : 'text-text-muted hover:bg-bg-hover hover:text-text-primary'
    }`;

  const handleLink = () => {
    if (editor.isActive('link')) {
      editor.chain().focus().unsetLink().run();
      return;
    }
    const url = window.prompt(t('richText.linkPrompt', 'Enter the link URL:'));
    if (url) {
      editor.chain().focus().setLink({ href: url }).run();
    }
  };

  return (
    <div>
      {label && (
        <label className="block text-sm text-text-secondary mb-1.5">{label}</label>
      )}
      <div className={`rounded-lg border bg-bg-field overflow-hidden ${error ? 'border-danger' : 'border-border-default focus-within:border-accent'}`}>
        {/* Toolbar */}
        <div className="flex flex-wrap items-center gap-0.5 border-b border-border-default bg-bg-tertiary px-2 py-1.5">
          <button type="button" onClick={() => editor.chain().focus().toggleBold().run()} className={btnClass(editor.isActive('bold'))} title={t('richText.bold', 'Bold')}>
            <Bold className="h-4 w-4" />
          </button>
          <button type="button" onClick={() => editor.chain().focus().toggleItalic().run()} className={btnClass(editor.isActive('italic'))} title={t('richText.italic', 'Italic')}>
            <Italic className="h-4 w-4" />
          </button>
          <button type="button" onClick={() => editor.chain().focus().toggleUnderline().run()} className={btnClass(editor.isActive('underline'))} title={t('richText.underline', 'Underline')}>
            <UnderlineIcon className="h-4 w-4" />
          </button>
          <button type="button" onClick={() => editor.chain().focus().toggleStrike().run()} className={btnClass(editor.isActive('strike'))} title={t('richText.strikethrough', 'Strikethrough')}>
            <Strikethrough className="h-4 w-4" />
          </button>

          <div className="mx-1 h-5 w-px bg-border-default" />

          <button type="button" onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} className={btnClass(editor.isActive('heading', { level: 2 }))} title={t('richText.heading2', 'Heading 2')}>
            <Heading2 className="h-4 w-4" />
          </button>
          <button type="button" onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} className={btnClass(editor.isActive('heading', { level: 3 }))} title={t('richText.heading3', 'Heading 3')}>
            <Heading3 className="h-4 w-4" />
          </button>

          <div className="mx-1 h-5 w-px bg-border-default" />

          <button type="button" onClick={() => editor.chain().focus().toggleBulletList().run()} className={btnClass(editor.isActive('bulletList'))} title={t('richText.bulletList', 'Bullet list')}>
            <List className="h-4 w-4" />
          </button>
          <button type="button" onClick={() => editor.chain().focus().toggleOrderedList().run()} className={btnClass(editor.isActive('orderedList'))} title={t('richText.orderedList', 'Ordered list')}>
            <ListOrdered className="h-4 w-4" />
          </button>

          <div className="mx-1 h-5 w-px bg-border-default" />

          <button type="button" onClick={() => editor.chain().focus().setTextAlign('left').run()} className={btnClass(editor.isActive({ textAlign: 'left' }))} title={t('richText.alignLeft', 'Align left')}>
            <AlignLeft className="h-4 w-4" />
          </button>
          <button type="button" onClick={() => editor.chain().focus().setTextAlign('center').run()} className={btnClass(editor.isActive({ textAlign: 'center' }))} title={t('richText.alignCenter', 'Align center')}>
            <AlignCenter className="h-4 w-4" />
          </button>
          <button type="button" onClick={() => editor.chain().focus().setTextAlign('right').run()} className={btnClass(editor.isActive({ textAlign: 'right' }))} title={t('richText.alignRight', 'Align right')}>
            <AlignRight className="h-4 w-4" />
          </button>

          <div className="mx-1 h-5 w-px bg-border-default" />

          <button type="button" onClick={handleLink} className={btnClass(editor.isActive('link'))} title={t('richText.link', 'Link')}>
            <LinkIcon className="h-4 w-4" />
          </button>

          <div className="flex-1" />

          <button type="button" onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().undo()} className="rounded p-1.5 text-text-muted hover:bg-bg-hover disabled:opacity-30" title={t('richText.undo', 'Undo')}>
            <Undo className="h-4 w-4" />
          </button>
          <button type="button" onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().redo()} className="rounded p-1.5 text-text-muted hover:bg-bg-hover disabled:opacity-30" title={t('richText.redo', 'Redo')}>
            <Redo className="h-4 w-4" />
          </button>
        </div>

        {/* Editor */}
        <EditorContent editor={editor} />
      </div>
      {error && <p className="mt-1 text-xs text-danger">{error}</p>}
    </div>
  );
}
